import { useEffect } from 'react';
import { useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

import theme from '../theme';
import { getPostsByType } from '../writings/posts/index.js';
import {
  mono,
  formatDate,
  TextLink,
  SiteHeader,
  SiteFooter,
  SectionLabel,
  PostList,
  Contact
} from './site-chrome.js';
import '../App.css';

// Shorthand for theme access
const t = theme;

const FEATURED_POSTS_COUNT = 4;

const socialLinks = [
  { text: 'LinkedIn', href: 'https://www.linkedin.com/in/arek-janeczko/' },
  { text: 'GitHub', href: 'https://github.com/ajaneczko' },
  { text: 'X', href: 'https://x.com/arekjaneczko' }
];

const logoFiles = [
  'image 1.png',
  'image 6.png',
  'image 7.png',
  'image 8.png',
  'image 9.png',
  'image 10.png',
  'image 12.png',
  'image 23.png',
  'image 30.png'
];

function Section({ id, index, label, action, children }) {
  return (
    <Box component="section" id={id} sx={{ py: { xs: 5, md: 7 }, scrollMarginTop: '80px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <SectionLabel index={index}>{label}</SectionLabel>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function Intro() {
  return (
    <Box component="section" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 5, md: 7 } }}>
      <Box
        component="img"
        src="/arekphoto3.jpg"
        alt="Arkadiusz Janeczko"
        sx={{
          width: 72,
          height: 72,
          borderRadius: t.borderRadius.full,
          objectFit: 'cover',
          mb: 4
        }}
      />
      <Typography
        component="h1"
        sx={{
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          fontWeight: t.typography.fontWeight.medium,
          lineHeight: t.typography.lineHeight.tight,
          letterSpacing: t.typography.letterSpacing.tight,
          color: t.colors.textPrimary,
          mb: 2.5
        }}
      >
        Arkadiusz Janeczko
        <Box component="span" sx={{ display: 'block', color: t.colors.textMuted }}>
          Product designer who ships.
        </Box>
      </Typography>
      <Typography
        sx={{
          fontSize: t.typography.fontSize.lg,
          lineHeight: t.typography.lineHeight.relaxed,
          color: t.colors.textSecondary,
          maxWidth: 560,
          mb: 3
        }}
      >
        I work across design, strategy and execution, leading design teams to
        build scalable SaaS products — bridging the gap between design,
        product and engineering.
      </Typography>
      <Box
        sx={{
          fontFamily: mono,
          fontSize: t.typography.fontSize.sm,
          color: t.colors.textMuted,
          lineHeight: 1.9,
          mb: 4
        }}
      >
        <div>
          <Box component="span" sx={{ color: t.colors.accent }}>●</Box> Senior
          Product Designer @{' '}
          <TextLink href="https://www.zyte.com/" external>
            Zyte
          </TextLink>
        </div>
        <div>→ Former founder @ Formly</div>
        <div>→ Based in Gdańsk, PL</div>
      </Box>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {socialLinks.map((link) => (
          <TextLink key={link.text} href={link.href} external>
            {link.text} ↗
          </TextLink>
        ))}
        <TextLink href="/Arkadiusz-Janeczko-CV.pdf" download>
          Download CV ↓
        </TextLink>
      </Box>
    </Box>
  );
}

function WorkCard({ post }) {
  return (
    <Link
      component={RouterLink}
      to={`/writings/${post.slug}`}
      underline="none"
      sx={{
        display: 'block',
        color: 'inherit',
        '&:hover img': { transform: 'scale(1.03)' },
        '&:hover .work-title': { color: t.colors.textPrimary }
      }}
    >
      <Box
        sx={{
          aspectRatio: '4 / 3',
          borderRadius: t.borderRadius.lg,
          overflow: 'hidden',
          backgroundColor: t.colors.backgroundCard,
          border: `1px solid ${t.colors.border}`,
          mb: 1.5
        }}
      >
        {post.cover && (
          <Box
            component="img"
            src={post.cover}
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: t.transitions.smooth
            }}
          />
        )}
      </Box>
      <Typography
        className="work-title"
        sx={{
          fontSize: t.typography.fontSize.md,
          fontWeight: t.typography.fontWeight.medium,
          color: t.colors.textLight,
          lineHeight: t.typography.lineHeight.normal,
          transition: t.transitions.fast
        }}
      >
        {post.title}
      </Typography>
      <Typography
        sx={{
          fontFamily: mono,
          fontSize: t.typography.fontSize.xs,
          color: t.colors.textMuted,
          mt: 0.5
        }}
      >
        Case study · {formatDate(post.date)}
      </Typography>
    </Link>
  );
}

function Logos() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: { xs: 3, md: 4 }
      }}
    >
      {logoFiles.map((logo, index) => (
        <Box
          key={logo}
          component="img"
          src={`/logos/${logo}`}
          alt={`Company logo ${index + 1}`}
          sx={{
            height: { xs: 22, md: 28 },
            width: 'auto',
            filter: 'grayscale(100%)',
            opacity: 0.5,
            transition: t.transitions.normal,
            '&:hover': { filter: 'grayscale(0%)', opacity: 1 },
            // Logos are dark artwork; flip them to light on the dark background
            '[data-theme="dark"] &': { filter: 'grayscale(100%) invert(1)', opacity: 0.6 },
            '[data-theme="dark"] &:hover': { filter: 'grayscale(100%) invert(1)', opacity: 1 }
          }}
        />
      ))}
    </Box>
  );
}

// Main Landing Page
function LandingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const caseStudies = getPostsByType('case-study');
  const featuredPosts = getPostsByType('post').slice(0, FEATURED_POSTS_COUNT);

  const location = useLocation();

  // Scroll to #section when arriving from another page (router doesn't do it)
  useEffect(() => {
    const id = location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, [location.key, location.hash]);

  // Legacy ?filter= links scroll to the matching section
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (!filter) return;
    const target = { projects: 'work', writing: 'writing' }[filter];
    if (target) document.getElementById(target)?.scrollIntoView();
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: t.colors.background }}>
      <SiteHeader />
      <Box
        component="main"
        sx={{ maxWidth: t.layout.contentWidth, mx: 'auto', px: { xs: 2, md: 3 } }}
      >
        <Intro />

        <Section id="work" index="01" label="Selected work">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: { xs: 4, sm: 3 }
            }}
          >
            {caseStudies.map((post) => (
              <WorkCard key={post.slug} post={post} />
            ))}
          </Box>
        </Section>

        <Section
          id="writing"
          index="02"
          label="Writing"
          action={<TextLink to="/writings/">All posts →</TextLink>}
        >
          <PostList posts={featuredPosts} />
        </Section>

        <Section index="03" label="Worked with">
          <Logos />
        </Section>

        <Section id="contact" index="04" label="Contact">
          <Contact />
        </Section>

        <SiteFooter />
      </Box>
    </Box>
  );
}

export default LandingPage;
